/**
 * The session boundary: the instant the signed-in identity changes (sign-out, or
 * a different Clerk user without a sign-out in between).
 *
 * Actors are torn down by the user state machine, but work that is already running
 * (HTTP requests, token refreshes, sockets, dialog callbacks, delayed navigation)
 * is not owned by an actor and survives it. Every such continuation captures the
 * session it started in and checks it before acting, so one account's results never
 * reach the next account.
 */

let epoch = 0;
const listeners = new Set<() => void>();

export const getSessionEpoch = (): number => epoch;

export const advanceSessionEpoch = (): void => {
  epoch += 1;
  // Copy first: a listener may unsubscribe while we iterate.
  [...listeners].forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error('[sessionBoundary] listener failed', error);
    }
  });
};

export const subscribeSessionBoundary = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export type CapturedSession = {
  epoch: number;
  /** True while no session boundary has been crossed since the capture. */
  isCurrent: () => boolean;
};

export const captureSession = (): CapturedSession => {
  const captured = epoch;
  return { epoch: captured, isCurrent: () => captured === epoch };
};
